import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import FlippexLoader from '../components/ui/FlippexLoader';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MarkdownText from '../components/MarkdownText';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { useAuth, api } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import TokenPriceChart from '../components/TokenPriceChart';
import ProjectFinancialsSection from '../components/ProjectFinancialsSection';
import InstrumentSummary from '../components/instrument/InstrumentSummary';
import { InvestmentTermsSection, AssetSnapshotSection } from '../components/ProjectInvestmentSummary';
import { PropertyHero } from '../components/project/PropertyHero';
import PropertyDetailSheet from '../components/project/PropertyDetailSheet';
import { ExitPerformanceHeader, ProjectedVsActual } from '../components/project/ExitPerformance';
import { Metric, FundingProgress } from '../components/marketplace/OpportunityCard';
import { projectTypeMeta } from '../components/marketplace/projectTypeMeta';
import {
  TheOpportunity,
  ProjectPlan,
  InvestmentThesis,
  UnderlyingProperties,
} from '../components/project/ProjectOverviewSections';
import ProjectTimeline from '../components/ProjectTimeline';
import InvestmentCalculator from '../components/InvestmentCalculator';
import { 
  SponsorProfileSection, 
  CapitalStackSection, 
  GovernanceSection, 
  RiskFactorsSection, 
  ExitStrategySection, 
  ProjectFAQSection,
  ComplianceNoticeSection 
} from '../components/ProjectSponsorInfo';
import {
  ArrowLeft,
  ArrowRight,
  MapPin,
  TrendUp,
  Clock,
  Coins,
  ChartLineUp,
  FileText,
  Buildings,
  Hammer,
  Stack,
  Tag,
  CalendarBlank,
  CurrencyEur,
  CaretLeft,
  CaretRight,
  CaretDown,
  CaretUp,
  X,
  Calculator,
  CreditCard,
  Bank,
  CheckCircle,
  ShieldWarning,
  Warning,
  ChatCircle,
  Target,
  Info,
  House,
  Ruler,
  Bed,
  Bathtub,
  Car,
  Certificate,
  Percent,
  Lock,
} from '@phosphor-icons/react';
import {
  getStatusLabel,
  getProjectTypeLabel,
  getAssetTypeLabel,
  STATUS_COLORS_SOLID,
  formatPublishedDate
} from '../lib/projectUtils';

// Project Type Icon Component
const ProjectTypeIcon = ({ type, className = "h-4 w-4" }) => {
  const Icon = projectTypeMeta(type).icon;
  return <Icon className={className} weight="duotone" />;
};

const ProjectDetailPage = ({ context = 'public' }) => {
  const isApp = context === 'app';
  const basePath = isApp ? '/app/explore' : '/projects';
  const onboardingPath = isApp ? '/app/onboarding' : '/onboarding';
  const portfolioPath = isApp ? '/app/portfolio' : '/portfolio';
  const [ownedHolding, setOwnedHolding] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, setShowAuthModal } = useAuth();
  const [project, setProject] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [showInvestModal, setShowInvestModal] = useState(false);
  const [investMode, setInvestMode] = useState('amount');
  const [investTokens, setInvestTokens] = useState('');
  const [investAmount, setInvestAmount] = useState('');
  const [investStep, setInvestStep] = useState(1);
  const [investing, setInvesting] = useState(false);
  const [amountError, setAmountError] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [acknowledgements, setAcknowledgements] = useState({
    risk: false,
    illiquidity: false
  });
  const [activeTab, setActiveTab] = useState('overview');
  const [showCalculatorModal, setShowCalculatorModal] = useState(false);
  const [userTransactionCount, setUserTransactionCount] = useState(0);
  const [assets, setAssets] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [expandedAssetId, setExpandedAssetId] = useState(null);
  const [overview, setOverview] = useState(null);
  const [sheetAsset, setSheetAsset] = useState(null);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const investCardRef = useRef(null);
  const [expandedInstrumentId, setExpandedInstrumentId] = useState(null);
  const tabContentRefs = useRef({});
  const stickyHeaderRef = useRef(null);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    // Scroll so the tab content starts right below the sticky header - never further
    setTimeout(() => {
      const contentRef = tabContentRefs.current[newTab];
      const stickyHeader = stickyHeaderRef.current;

      if (contentRef && stickyHeader) {
        const headerHeight = 64;
        const stickyHeaderHeight = stickyHeader.offsetHeight;
        const contentRect = contentRef.getBoundingClientRect();
        const targetViewportPosition = headerHeight + stickyHeaderHeight + 16;
        const targetScroll = window.scrollY + contentRect.top - targetViewportPosition;

        window.scrollTo({ top: Math.max(targetScroll, 0), behavior: 'smooth' });
      }
    }, 100);
  };

  useEffect(() => {
    const onScroll = () => {
      const card = investCardRef.current;
      const cardTop = card ? card.getBoundingClientRect().top : Infinity;
      setShowStickyCta(window.scrollY > 200 && cardTop > window.innerHeight - 120);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    fetchProject();
    fetchUpdates();
    fetchAssets();
    fetchOverview();
    fetchInstruments();
    if (user) {
      fetchProfile();
      fetchUserTransactionCount();
      if (isApp) fetchOwnedHolding();
    }
  }, [id, user]);

  useEffect(() => {
    if (searchParams.get('invest') === 'true' && user && project && !loading) {
      if (profile?.onboarding_status === 'completed') {
        setShowInvestModal(true);
      }
      setSearchParams({});
    }
  }, [searchParams, user, project, loading, profile]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${id}`);
      setProject(response.data);
    } catch (error) {
      console.error('Error fetching project:', error);
      toast.error('Project not found');
      navigate(basePath);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpdates = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${id}/updates`);
      setUpdates(response.data);
    } catch (error) {
      console.error('Error fetching updates:', error);
    }
  };

  const fetchOwnedHolding = async () => {
    try {
      const response = await api.get('/portfolio/v2/home');
      const match = (response.data.holdings || []).find(
        (h) => h.project_id === id || h.project_code === id
      );
      setOwnedHolding(match || null);
    } catch (error) {
      setOwnedHolding(null);
    }
  };

  const fetchOverview = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${id}/overview`);
      setOverview(response.data);
    } catch (error) {
      setOverview(null);
    }
  };

  const fetchAssets = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${id}/assets`);
      setAssets(response.data.assets || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  };

  const fetchInstruments = async () => {
    try {
      // Use api client with auth if user is logged in, otherwise use axios
      if (user) {
        const response = await api.get(`/projects/${id}/instruments`);
        setInstruments(response.data.instruments || []);
      } else {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${id}/instruments`);
        setInstruments(response.data.instruments || []);
      }
    } catch (error) {
      console.error('Error fetching instruments:', error);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await api.get('/onboarding/profile');
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchUserTransactionCount = async () => {
    try {
      const response = await api.get(`/investor/transactions?project_id=${id}`);
      setUserTransactionCount(response.data?.length || 0);
    } catch (error) {
      console.error('Error fetching transaction count:', error);
      setUserTransactionCount(0);
    }
  };

  const generatePaymentReference = () => {
    const fiscalId = profile?.fiscal_id || profile?.nif || 'PENDING';
    const projectShortId = id?.slice(0, 8) || 'PROJ';
    const txCount = (userTransactionCount + 1).toString().padStart(3, '0');
    return `${fiscalId}-${projectShortId}-${txCount}`;
  };

  const canInvest = profile?.eligibility_status === 'eligible' ||
                    profile?.onboarding_status === 'completed' || 
                    profile?.kyc_status === 'approved' ||
                    profile?.kyc_status === 'verified' ||
                    profile?.onboarding_step === 'complete';

  // Get images - prioritize first asset's images, then fall back to project images
  const getProjectImages = () => {
    if (assets.length > 0 && assets[0].image_urls?.length > 0) {
      return assets[0].image_urls;
    }
    return project?.image_urls || [];
  };

  const projectImages = getProjectImages();

  const calculateInvestment = () => {
    const tokenPrice = project?.token_price || 0;
    if (investMode === 'tokens') {
      const tokens = parseFloat(investTokens) || 0;
      return {
        tokens,
        amount: tokens * tokenPrice,
        isValid: tokens > 0 && tokens * tokenPrice >= (project?.min_investment || 0)
      };
    } else {
      const amount = parseFloat(investAmount) || 0;
      const tokens = tokenPrice > 0 ? amount / tokenPrice : 0;
      const isMultiple = tokenPrice > 0 ? amount % tokenPrice === 0 : false;
      return {
        tokens,
        amount,
        isValid: amount >= (project?.min_investment || 0) && isMultiple
      };
    }
  };

  const handleAmountChange = (value) => {
    setInvestAmount(value);
    const amount = parseFloat(value) || 0;
    const tokenPrice = project?.token_price || 0;
    
    if (amount > 0 && tokenPrice > 0 && amount % tokenPrice !== 0) {
      const lowerMultiple = Math.floor(amount / tokenPrice) * tokenPrice;
      const higherMultiple = Math.ceil(amount / tokenPrice) * tokenPrice;
      setAmountError(`Amount must be a multiple of unit price (€${tokenPrice}). Try €${lowerMultiple.toLocaleString()} or €${higherMultiple.toLocaleString()}`);
    } else if (amount > 0 && amount < (project?.min_investment || 0)) {
      setAmountError(`Minimum investment is €${project?.min_investment?.toLocaleString()}`);
    } else {
      setAmountError('');
    }
  };

  const handleTokensChange = (value) => {
    setInvestTokens(value);
    const tokens = parseFloat(value) || 0;
    const amount = tokens * (project?.token_price || 0);
    if (amount > 0 && amount < (project?.min_investment || 0)) {
      setAmountError(`Minimum investment is €${project?.min_investment?.toLocaleString()} (${Math.ceil(project.min_investment / project.token_price)} units)`);
    } else {
      setAmountError('');
    }
  };

  const resetInvestModal = () => {
    setInvestStep(1);
    setInvestTokens('');
    setInvestAmount('');
    setAmountError('');
    setAcknowledgements({ risk: false, illiquidity: false });
  };

  const handleInvest = async () => {
    if (!user) {
      toast.error('Please login to invest');
      return;
    }

    if (profile?.eligibility_status !== 'eligible') {
      toast.error('Please complete onboarding and verification first');
      navigate(onboardingPath);
      return;
    }

    const { tokens, amount, isValid } = calculateInvestment();
    
    if (!isValid) {
      toast.error(`Invalid investment amount`);
      return;
    }

    if (amount < project.min_investment) {
      toast.error(`Minimum investment is €${project.min_investment}`);
      return;
    }

    if (!acknowledgements.risk || !acknowledgements.illiquidity) {
      toast.error('Please acknowledge all risk factors');
      return;
    }

    // The instrument IS the offering
    const activeInstrument = instruments.find(i => i.status === 'active');
    if (!activeInstrument) {
      toast.error('No active investment offering available for this project');
      return;
    }

    try {
      setInvesting(true);
      const response = await api.post('/investments/subscribe', {
        security_id: activeInstrument.id,
        units: Math.floor(tokens),
        notes: `Investment in ${project.title}`
      });
      toast.success(response.data.message || 'Investment subscription created!');
      setShowInvestModal(false);
      resetInvestModal();
      navigate(portfolioPath);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Investment failed');
    } finally {
      setInvesting(false);
    }
  };

  // Opening a project resolves in a blink; keeping the chrome in place avoids a flash of empty page.
  if (loading) {
    return (
      <div
        className={isApp ? '' : 'min-h-screen bg-[rgb(var(--background))]'}
        data-testid="project-detail-loading"
      >
        {!isApp && <Header />}
        <div className="min-h-[60vh]" />
      </div>
    );
  }

  if (!project) return null;

  const isEligible = user && profile?.eligibility_status === 'eligible';
  const isExited = project.lifecycle === 'exited';
  const canInvestNow = !isExited && isEligible && ['open', 'funding', 'published', 'active'].includes(project.status);

  // Location, strategy and date: under the title on mobile, on the tab row on desktop
  const projectMeta = (
    <>
      {project.location && project.location.trim() !== ',' && project.location.trim() !== '' && (
        <>
          <span className="flex items-center gap-1.5 flex-shrink-0">
            <MapPin size={14} weight="bold" className="text-primary" />
            {project.location}
          </span>
          <span className="text-black/20 flex-shrink-0">|</span>
        </>
      )}
      <span
        className={`inline-flex items-center gap-1.5 text-[11px] md:text-xs font-medium flex-shrink-0 ${projectTypeMeta(project.project_type).ink}`}
        data-testid="project-strategy-pill"
      >
        {React.createElement(projectTypeMeta(project.project_type).icon, { size: 14, weight: 'bold' })}
        {project.project_type_label || getProjectTypeLabel(project.project_type)}
      </span>
      {(isExited ? project.performance?.exit_date_label : project.published_at) && (
        <>
          <span className="text-black/20 flex-shrink-0">|</span>
          <span className="flex items-center gap-1.5 text-[11px] md:text-xs text-foreground-muted/80 flex-shrink-0">
            <CalendarBlank size={13} />
            {isExited
              ? `Exited ${project.performance.exit_date_label}`
              : `Listed ${formatPublishedDate(project.published_at)}`}
          </span>
        </>
      )}
    </>
  );
  const fundingPercentage = (project.funding_current / project.funding_target) * 100;

  return (
    <div className={isApp ? '' : 'min-h-screen bg-[rgb(var(--background))]'} data-testid="project-detail-page">
      {!isApp && <Header />}

      <motion.div
        className="pt-4 pb-8"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          {/* Back Button */}
          <div className="mb-3">
            <Button
              variant="ghost"
              onClick={() => navigate(basePath)}
              className="text-foreground-muted hover:text-foreground hover:bg-black/5 rounded-full px-4"
              data-testid="back-to-projects-button"
            >
              <ArrowLeft size={18} className="mr-2" />
              {isApp ? 'Explore' : 'Back to Projects'}
            </Button>
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Images, Info, Tabs */}
            <div className="lg:col-span-2">
              {/* You're invested - authenticated ownership context */}
              {isApp && ownedHolding && (
                <button
                  onClick={() => navigate(`/app/portfolio/${ownedHolding.security_id}`)}
                  className="w-full text-left mb-5 rounded-2xl border border-primary/25 bg-primary/[0.06] px-5 py-4 flex items-center justify-between gap-4 hover:border-primary/50 transition-colors"
                  data-testid="youre-invested-banner"
                >
                  <span className="min-w-0">
                    <span className="block text-[11px] uppercase tracking-[0.16em] text-primary font-medium">
                      You're invested
                    </span>
                    <span className="block text-sm text-foreground-muted mt-1 tabular-nums">
                      €{Number(ownedHolding.invested || 0).toLocaleString('en-GB')} invested · {ownedHolding.units} shares
                    </span>
                  </span>
                  <span className="text-sm font-medium text-primary inline-flex items-center gap-1.5 flex-shrink-0">
                    View my investment <ArrowRight size={14} weight="bold" />
                  </span>
                </button>
              )}

              {/* Sticky Project Header */}
              <div 
                ref={stickyHeaderRef} 
                className="sticky top-16 z-30 bg-[rgb(var(--background))] py-4 border-b border-black/[0.04] -mx-6 px-6 md:-mx-12 md:px-12 lg:mx-0 lg:px-0"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <h1 className="text-2xl md:text-3xl font-heading font-semibold" data-testid="project-title">
                      {project.title}
                    </h1>
                    {/* On desktop this meta sits on the tab row instead, flush right */}
                    <div className="flex md:hidden items-center gap-2 mt-2 text-xs text-foreground-muted whitespace-nowrap overflow-x-auto scrollbar-hide">
                      {projectMeta}
                    </div>
                  </div>
                </div>

                {/* Tabs, with the project meta flush right on desktop */}
                <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full mt-4">
                  <div className="flex items-center justify-between gap-6">
                  <TabsList className="w-full grid grid-cols-3 gap-2 md:w-auto md:flex md:justify-start bg-transparent p-0 border-0">
                    {[
                      { value: 'overview', label: 'Overview', testId: 'tab-overview' },
                      { value: 'financials', label: 'Financials', testId: 'tab-financials' },
                      { value: 'updates', label: 'Updates', testId: 'tab-updates' },
                    ].map((tab) => (
                      <TabsTrigger
                        key={tab.value}
                        value={tab.value}
                        data-testid={tab.testId}
                        className="w-full md:w-auto px-3.5 h-[29px] text-[13px] font-medium rounded-full border-0 transition-all whitespace-nowrap
                          data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm
                          data-[state=inactive]:bg-black/[0.04] data-[state=inactive]:text-foreground-muted data-[state=inactive]:hover:bg-black/[0.08]"
                      >
                        {tab.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                    <div
                      className="hidden md:flex items-center gap-3 text-xs lg:text-sm text-foreground-muted whitespace-nowrap flex-shrink-0"
                      data-testid="project-meta-desktop"
                    >
                      {projectMeta}
                    </div>
                  </div>
                </Tabs>
              </div>

              {/* Tab Contents */}
              <div className="mt-8">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div ref={el => tabContentRefs.current['overview'] = el} className="space-y-14">
                    {(() => {
                      const primaryAsset = assets[0] || null;
                      const heroImages = (primaryAsset?.image_urls?.length ? primaryAsset.image_urls : project.image_urls) || [];
                      const navOwnership = instruments?.[0]?.nav_ownership;
                      const flippexPct = navOwnership?.flippex_percentage ?? navOwnership?.flippex_pct;
                      const city = primaryAsset?.city || project.city || overview?.location?.city;
                      const country = primaryAsset?.country_code || project.country_code || overview?.location?.country_code;
                      const projectCtx = { ...project, city, country_code: country };
                      const drivers = [];
                      if (flippexPct !== null && flippexPct !== undefined) {
                        drivers.push({
                          title: 'Aligned capital',
                          body: `Flippex co-invests alongside investors and retains ${Number(flippexPct).toFixed(2)}% of the project economics.`,
                        });
                      }
                      return (
                        <>
                          <PropertyHero
                            asset={primaryAsset}
                            project={projectCtx}
                            images={heroImages}
                            onOpenDetails={primaryAsset ? () => setSheetAsset(primaryAsset) : null}
                          />
                          {assets.length > 1 && (
                            <UnderlyingProperties assets={assets} project={projectCtx} onOpen={setSheetAsset} />
                          )}
                          {project.lifecycle === 'exited' && (
                            <div className="lg:hidden">
                              <ExitPerformanceHeader
                                performance={project.performance}
                                onViewFinancials={() => handleTabChange('financials')}
                              />
                            </div>
                          )}
                          <TheOpportunity project={project} />
                          <ProjectPlan plan={overview?.plan} nextMilestone={overview?.next_milestone} />
                          {project.lifecycle === 'exited' && <ProjectedVsActual performance={project.performance} />}
                          <InvestmentThesis project={project} drivers={drivers} />
                          {sheetAsset && (
                            <PropertyDetailSheet
                              asset={sheetAsset}
                              project={projectCtx}
                              economics={overview?.economics}
                              onClose={() => setSheetAsset(null)}
                              onViewFinancials={() => { setSheetAsset(null); handleTabChange('financials'); }}
                            />
                          )}
                        </>
                      );
                    })()}

                    {/* Exit Strategy */}
                    <ExitStrategySection project={project} />

                    {/* Sponsor Profile */}
                    <SponsorProfileSection project={project} />

                    {/* FAQ */}
                    <ProjectFAQSection project={project} />
                  </div>
                )}

                {/* Financials Tab */}
                {activeTab === 'financials' && (
                  <div ref={el => tabContentRefs.current['financials'] = el} className="space-y-10">
                    {/* Investment Instrument - one responsive experience */}
                    {instruments.length > 0 ? (
                      <InstrumentSummary
                        instrument={instruments[0]}
                        currency={instruments[0].currency || 'EUR'}
                      />
                    ) : project.has_instruments ? (
                      <div className="bg-white rounded-2xl border border-black/[0.05] px-6 py-10 text-center text-foreground-muted">
                        <Coins size={32} className="mx-auto mb-2 opacity-50" />
                        <p>Loading instrument details...</p>
                      </div>
                    ) : (
                      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
                        <Warning size={32} className="text-amber-500 mx-auto mb-3" weight="duotone" />
                        <p className="text-amber-800 font-medium">Investment Instrument Pending</p>
                        <p className="text-sm text-amber-600 mt-1">
                          Financial terms will be available once an investment instrument is configured.
                        </p>
                      </div>
                    )}

                    {/* Capital Stack */}
                    <CapitalStackSection project={project} />

                    {/* Financials / Deal Room */}
                    <ProjectFinancialsSection projectId={id} />

                    {/* Governance & Compliance */}
                    <GovernanceSection project={project} />
                    <ComplianceNoticeSection project={project} />

                    {/* Risk Factors */}
                    <RiskFactorsSection project={project} />
                  </div>
                )}

                {/* Updates Tab (renamed from Timeline & Updates) */}
                {activeTab === 'updates' && (
                  <div ref={el => tabContentRefs.current['updates'] = el} className="space-y-10">
                    {/* Project Updates */}
                    <section className="bg-white rounded-2xl border border-black/[0.05] px-5 py-5 md:px-7 md:py-6" data-testid="project-updates-section">
                      <div className="flex items-center gap-2">
                        <ChatCircle size={17} className="text-primary" weight="duotone" />
                        <h3 className="text-sm md:text-base font-heading">Project Updates ({updates.length})</h3>
                      </div>

                      {updates.length === 0 ? (
                        <div className="mt-5 md:mt-6 pt-6 border-t border-black/[0.06] text-center pb-4">
                          <ChatCircle size={28} className="text-primary/50 mx-auto mb-3" weight="duotone" />
                          <p className="text-sm font-medium">No updates yet</p>
                          <p className="mt-1 text-xs md:text-sm text-foreground-muted">Project updates will appear here once published</p>
                        </div>
                      ) : (
                        <div className="mt-5 md:mt-6">
                          {updates.map((update, idx) => {
                            const isLast = idx === updates.length - 1;
                            return (
                              <div
                                key={update.id}
                                data-testid={`update-${update.id}`}
                                className={`flex gap-3 md:gap-4 ${idx === 0 ? '' : 'border-t border-black/[0.06] pt-5 md:pt-6'} ${isLast ? '' : 'pb-5 md:pb-6'}`}
                              >
                                <div className="w-[68px] md:w-[92px] flex-shrink-0 pt-px text-right">
                                  <p
                                    className="text-[11px] md:text-xs text-foreground-muted tabular-nums leading-snug"
                                    data-testid={`update-date-${update.id}`}
                                  >
                                    {new Date(update.publishing_date || update.created_at).toLocaleDateString('en-GB', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric',
                                    })}
                                  </p>
                                </div>

                                <div className="relative w-3 flex-shrink-0 flex justify-center">
                                  <div
                                    className={`absolute left-1/2 -translate-x-1/2 w-px bg-black/[0.08] ${isLast ? 'top-0 h-[7px]' : 'inset-y-0'}`}
                                  />
                                  <div className="relative mt-[3px] w-2 h-2 rounded-full bg-primary ring-4 ring-white" />
                                </div>

                                <div className="flex-1 min-w-0">
                                  <h4 className="text-[10px] md:text-[11px] uppercase tracking-[0.16em] text-foreground-muted">
                                    {update.title}
                                  </h4>
                                  <p className="mt-2 text-xs md:text-sm text-foreground-muted leading-relaxed whitespace-pre-wrap">
                                    {update.content}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </section>

                    {/* Investor Reporting */}
                    <section className="bg-white rounded-2xl border border-black/[0.05] px-5 py-5 md:px-7 md:py-6" data-testid="investor-reporting-section">
                      <div className="flex items-center gap-2">
                        <FileText size={17} className="text-primary" weight="duotone" />
                        <h3 className="text-sm md:text-base font-heading">Investor Reporting</h3>
                      </div>

                      <p className="mt-5 md:mt-6 pt-5 md:pt-6 border-t border-black/[0.06] text-xs md:text-sm text-foreground-muted leading-relaxed">
                        As an investor, you will receive regular reports on the project&apos;s performance, financials, and key milestones.
                      </p>

                      <div className="mt-5 md:mt-6">
                        {[
                          { icon: CalendarBlank, title: 'Quarterly Reports', desc: 'Financial statements, occupancy rates, and operational updates every quarter.' },
                          { icon: TrendUp, title: 'Performance Metrics', desc: 'Track ROI, distributions, and unit price evolution in real-time on your dashboard.' },
                          { icon: ChatCircle, title: 'Project Updates', desc: 'Stay informed with milestone notifications and important project announcements.' },
                          { icon: CurrencyEur, title: 'Distribution History', desc: 'Complete record of all dividend payments and rental income distributions.' },
                        ].map((item, idx, arr) => {
                          const Icon = item.icon;
                          const isLast = idx === arr.length - 1;
                          return (
                            <div
                              key={item.title}
                              className={`flex gap-3 md:gap-4 ${idx === 0 ? '' : 'border-t border-black/[0.06] pt-5 md:pt-6'} ${isLast ? '' : 'pb-5 md:pb-6'}`}
                            >
                              <Icon size={16} className="text-primary flex-shrink-0 mt-px" weight="duotone" />
                              <div className="flex-1 min-w-0">
                                <h4 className="text-[10px] md:text-[11px] uppercase tracking-[0.16em] text-foreground-muted">
                                  {item.title}
                                </h4>
                                <p className="mt-2 text-xs md:text-sm text-foreground-muted leading-relaxed">{item.desc}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Investment Card (Sticky) */}
            <div className="lg:col-span-1" ref={investCardRef}>
              {isExited ? (
                <div
                  className="lg:sticky lg:top-1/2 lg:-translate-y-1/2 space-y-4"
                  data-testid="exited-project-notice"
                >
                  <ExitPerformanceHeader
                    performance={project.performance}
                    onViewFinancials={() => handleTabChange('financials')}
                  />
                  <p className="text-xs text-foreground-muted leading-relaxed px-1">
                    This project has exited and is no longer open for investment. The figures above are the
                    realized result.
                  </p>
                </div>
              ) : (
              <Card className="bg-white rounded-2xl p-6 border border-black/[0.04] shadow-lg sticky top-24" data-testid="investment-card">
                {project.has_instruments ? (
                  <>
                    {/* The same three terms investors already read on the project cards */}
                    <div className="grid grid-cols-3 gap-3 mb-6" data-testid="invest-card-metrics">
                      <Metric
                        value={project.projected_return ? `${Number(project.projected_return).toFixed(1)}%` : '—'}
                        label="Projected IRR"
                        testId="invest-metric-return"
                      />
                      <Metric
                        value={project.target_hold_months ? `${project.target_hold_months} mo` : '—'}
                        label="Target hold"
                        testId="invest-metric-hold"
                      />
                      <Metric
                        value={project.min_investment > 0 ? `€${Number(project.min_investment).toLocaleString('en-GB')}` : '—'}
                        label="Minimum"
                        testId="invest-metric-minimum"
                      />
                    </div>

                    {project.funding_target > 0 && (
                      <div className="mb-6">
                        <FundingProgress progress={fundingPercentage} raised={project.funding_current} />
                      </div>
                    )}

                    {/* CTA Button */}
                    {!user ? (
                      <Button
                        className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-6 text-base font-medium"
                        onClick={() => setShowAuthModal(true)}
                        data-testid="login-to-invest-button"
                      >
                        Login to Invest
                      </Button>
                ) : !isEligible ? (
                  <div>
                    <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                      <div className="flex items-start space-x-3">
                        <Warning size={20} className="text-amber-600 mt-0.5 flex-shrink-0" weight="fill" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">Finish your profile</p>
                          <p className="text-xs text-amber-700 mt-1">
                            It takes about a minute, then you can invest
                          </p>
                        </div>
                      </div>
                    </div>
                    <Button
                      className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-6 text-base font-medium"
                      onClick={() => navigate(onboardingPath)}
                      data-testid="complete-onboarding-button"
                    >
                      Complete my profile
                    </Button>
                  </div>
                ) : !['open', 'funding', 'published', 'active'].includes(project.status) ? (
                  <Button className="w-full rounded-full py-6 text-base" disabled>
                    Not Available
                  </Button>
                ) : (
                  <Button
                    className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-6 text-base font-medium transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/25"
                    onClick={() => setShowInvestModal(true)}
                    data-testid="invest-button"
                  >
                    Invest Now
                  </Button>
                )}

                {/* Risk Disclaimer */}
                <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-xl">
                  <p className="text-xs text-amber-700 text-center leading-relaxed">
                    Investment involves risk. Past performance is not indicative of future results. Capital at risk.
                  </p>
                </div>

                {/* Calculator Button */}
                {project.funding_target > 0 && (
                  <Button
                    variant="outline"
                    className="w-full mt-4 border-primary/30 text-primary hover:bg-primary/5 rounded-full py-5"
                    onClick={() => setShowCalculatorModal(true)}
                    data-testid="open-calculator-btn"
                  >
                    <Calculator size={18} className="mr-2" weight="duotone" />
                    Simulate Returns
                  </Button>
                )}

                  </>
                ) : (
                  /* No instruments configured */
                  <div className="text-center py-4">
                    <Warning size={48} className="text-amber-500 mx-auto mb-4" weight="duotone" />
                    <h3 className="font-semibold text-lg mb-2">Investment Instrument Pending</h3>
                    <p className="text-sm text-foreground-muted mb-4">
                      This project is not yet open for investment. Financial terms and investment options will be available once an investment instrument is configured.
                    </p>
                    <div className="p-3 bg-[rgb(var(--background))] rounded-xl">
                      <p className="text-xs text-foreground-muted">Duration</p>
                      <p className="text-lg font-semibold">{project.duration_months || '—'} months</p>
                    </div>
                  </div>
                )}
              </Card>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Investment Calculator Modal */}
      <Dialog open={showCalculatorModal} onOpenChange={setShowCalculatorModal}>
        <DialogContent className="bg-white border-black/[0.08] max-w-md p-0 overflow-hidden rounded-2xl" data-testid="calculator-modal">
          <InvestmentCalculator project={project} />
        </DialogContent>
      </Dialog>

      {/* Investment Modal */}
      <Dialog open={showInvestModal} onOpenChange={(open) => { setShowInvestModal(open); if (!open) resetInvestModal(); }}>
        <DialogContent className="bg-white border-black/[0.08] rounded-2xl max-w-md p-0 overflow-hidden" data-testid="invest-modal">
          {/* Header */}
          <div className="pt-6 pb-4 px-6 text-center border-b border-black/[0.04]">
            <img 
              src="https://customer-assets.emergentagent.com/job_invest-portal-59/artifacts/c24cz0ev_Color%20logo%20-%20no%20background.png" 
              alt="Flippex Logo" 
              className="h-8 w-auto mx-auto mb-3"
            />
            <DialogTitle className="text-xl font-heading font-semibold">
              {investStep === 1 && `Invest in ${project.title}`}
              {investStep === 2 && 'Payment Details'}
              {investStep === 3 && 'Confirm Investment'}
            </DialogTitle>
            <DialogDescription className="sr-only">Investment flow for {project.title}</DialogDescription>
          </div>

          <div className="px-6 pb-6">
            {/* KYC Incomplete Warning */}
            {user && !canInvest && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <ShieldWarning size={20} className="text-amber-600 mt-0.5 flex-shrink-0" weight="fill" />
                  <div>
                    <h4 className="font-semibold text-amber-800 text-sm">Verification Required</h4>
                    <p className="text-xs text-amber-700 mt-1">
                      Complete your identity verification to unlock investment tools.
                    </p>
                    <Button
                      size="sm"
                      className="mt-3 bg-amber-500 hover:bg-amber-600 text-white rounded-full"
                      onClick={() => navigate(onboardingPath)}
                    >
                      Complete Verification
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: Investment Input */}
            {investStep === 1 && canInvest && (
              <div className="space-y-4 mt-4">
                {/* Input Mode Toggle */}
                <div className="flex items-center justify-between p-3 bg-[rgb(var(--background))] rounded-xl">
                  <span className={`text-sm font-medium ${investMode === 'tokens' ? 'text-foreground' : 'text-foreground-muted'}`}>
                    By Units
                  </span>
                  <Switch
                    checked={investMode === 'amount'}
                    onCheckedChange={(checked) => {
                      setInvestMode(checked ? 'amount' : 'tokens');
                      setAmountError('');
                    }}
                    data-testid="invest-mode-toggle"
                  />
                  <span className={`text-sm font-medium ${investMode === 'amount' ? 'text-foreground' : 'text-foreground-muted'}`}>
                    By Amount
                  </span>
                </div>

                {/* Unit Input Mode */}
                {investMode === 'tokens' && (
                  <div>
                    <Label htmlFor="tokens" className="text-sm font-medium">Number of Units</Label>
                    <Input
                      id="tokens"
                      type="number"
                      placeholder="e.g. 10"
                      value={investTokens}
                      onChange={(e) => handleTokensChange(e.target.value)}
                      min={1}
                      data-testid="invest-tokens-input"
                      className="mt-1.5 rounded-xl border-black/10 focus:border-primary"
                    />
                    <div className="flex justify-between text-xs mt-2">
                      <span className="text-foreground-muted">Unit Price: €{project.token_price?.toLocaleString()}</span>
                      <span className="text-primary font-medium">
                        Total: €{(parseFloat(investTokens || 0) * project.token_price).toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}

                {/* Amount Input Mode */}
                {investMode === 'amount' && (
                  <div>
                    <Label htmlFor="amount" className="text-sm font-medium">Investment Amount (EUR)</Label>
                    <Input
                      id="amount"
                      type="number"
                      placeholder={project.min_investment.toString()}
                      value={investAmount}
                      onChange={(e) => handleAmountChange(e.target.value)}
                      min={project.min_investment}
                      step={project.token_price}
                      data-testid="invest-amount-input"
                      className={`mt-1.5 rounded-xl border-black/10 focus:border-primary ${amountError ? 'border-red-500' : ''}`}
                    />
                    <div className="flex justify-between text-xs mt-2">
                      <span className="text-foreground-muted">Unit Price: €{project.token_price?.toLocaleString()}</span>
                      <span className="text-primary font-medium">
                        Units: {project.token_price > 0 ? (parseFloat(investAmount || 0) / project.token_price).toFixed(0) : 0}
                      </span>
                    </div>
                    {amountError && (
                      <p className="text-xs text-red-500 mt-1">{amountError}</p>
                    )}
                  </div>
                )}

                <p className="text-xs text-foreground-muted">
                  Minimum investment: €{project.min_investment.toLocaleString()} ({Math.ceil(project.min_investment / project.token_price)} units)
                </p>

                {/* Investment Summary */}
                <div className="p-4 bg-[rgb(var(--background))] rounded-xl">
                  <h4 className="font-semibold mb-3 text-sm">Investment Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-foreground-muted">Units</span>
                      <span className="font-medium">{calculateInvestment().tokens.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-muted">Investment Amount</span>
                      <span className="font-medium">€{calculateInvestment().amount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-xs text-foreground-muted">
                      <span>Platform Fee (2.5%)</span>
                      <span>€{(calculateInvestment().amount * 0.025).toLocaleString()}</span>
                    </div>
                    <div className="border-t border-black/[0.04] pt-2 flex justify-between font-bold">
                      <span>Total</span>
                      <span className="text-primary">€{calculateInvestment().amount.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <Button
                  className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-5"
                  onClick={() => setInvestStep(2)}
                  disabled={!calculateInvestment().isValid || !!amountError}
                  data-testid="continue-to-payment"
                >
                  Continue to Payment
                </Button>
              </div>
            )}

            {/* Step 2: Payment Details */}
            {investStep === 2 && (
              <div className="space-y-4 mt-4">
                {/* Bank Transfer */}
                <div className="p-4 bg-[rgb(var(--background))] rounded-xl">
                  <div className="flex items-center gap-2 mb-4">
                    <Bank size={20} className="text-green-600" weight="duotone" />
                    <h4 className="font-semibold">Bank Transfer</h4>
                    <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-semibold rounded-full">
                      NO FEE
                    </span>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="p-3 bg-white rounded-lg border border-black/[0.04]">
                      <p className="text-foreground-muted text-xs mb-1">IBAN</p>
                      <p className="font-mono font-semibold text-sm">PT50 0018 0003 5471 9380 0201 1</p>
                    </div>
                    <div className="p-3 bg-white rounded-lg border border-black/[0.04]">
                      <p className="text-foreground-muted text-xs mb-1">BIC/SWIFT</p>
                      <p className="font-mono font-semibold">TOTAPTPL</p>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-muted">Beneficiary</span>
                      <span className="font-medium text-xs">FLIPPEX - SMART REAL ESTATE, LDA</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-muted">Reference</span>
                      <span className="font-mono font-medium text-primary">{generatePaymentReference()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-muted">Amount</span>
                      <span className="font-bold text-primary">€{calculateInvestment().amount.toLocaleString()}</span>
                    </div>
                  </div>
                  <p className="text-[10px] text-foreground-muted mt-3 text-center">
                    Use the exact reference above for faster processing
                  </p>
                </div>

                {/* Coming Soon */}
                <div className="p-4 bg-[rgb(var(--background))] rounded-xl border border-black/[0.04]">
                  <div className="flex items-center gap-2 mb-3">
                    <CreditCard size={20} className="text-blue-500" weight="duotone" />
                    <h4 className="font-semibold text-sm">Coming Soon</h4>
                  </div>
                  <div className="space-y-2 opacity-60">
                    {[
                      { name: 'Stripe', desc: 'Cards & Apple/Google Pay' },
                      { name: 'Revolut', desc: 'Instant bank transfers' },
                      { name: 'PayPal', desc: 'PayPal balance & cards' }
                    ].map((method) => (
                      <div key={method.name} className="flex items-center justify-between p-2 bg-white rounded-lg">
                        <div>
                          <span className="text-sm font-medium">{method.name}</span>
                          <span className="text-xs text-foreground-muted ml-2">{method.desc}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setInvestStep(1)} className="flex-1 rounded-full border-black/10">
                    Back
                  </Button>
                  <Button
                    className="flex-1 bg-primary hover:bg-primary-hover text-white rounded-full"
                    onClick={() => setInvestStep(3)}
                  >
                    Continue to Confirm
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Confirmation */}
            {investStep === 3 && (
              <div className="space-y-4 mt-4">
                <div className="p-4 bg-[rgb(var(--background))] rounded-xl">
                  <h4 className="font-semibold mb-3">Confirm Your Investment</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-foreground-muted">Project</span>
                      <span className="font-medium">{project.title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-muted">Units</span>
                      <span className="font-medium">{calculateInvestment().tokens.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-muted">Unit Price</span>
                      <span className="font-medium">€{project.token_price?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-muted">Payment Reference</span>
                      <span className="font-mono text-xs text-primary">{generatePaymentReference()}</span>
                    </div>
                    <div className="flex justify-between text-xs text-foreground-muted">
                      <span>Platform Fee (2.5%)</span>
                      <span>€{(calculateInvestment().amount * 0.025).toLocaleString()}</span>
                    </div>
                    <div className="border-t border-black/[0.04] pt-2 flex justify-between font-bold text-lg">
                      <span>Total Investment</span>
                      <span className="text-primary">€{calculateInvestment().amount.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="risk"
                      checked={acknowledgements.risk}
                      onCheckedChange={(checked) => setAcknowledgements({ ...acknowledgements, risk: checked })}
                      data-testid="risk-acknowledgement"
                      className="mt-0.5"
                    />
                    <label htmlFor="risk" className="text-sm text-foreground-muted cursor-pointer leading-relaxed">
                      I understand that this investment involves risk and I may lose some or all of my capital
                    </label>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="illiquidity"
                      checked={acknowledgements.illiquidity}
                      onCheckedChange={(checked) => setAcknowledgements({ ...acknowledgements, illiquidity: checked })}
                      data-testid="illiquidity-acknowledgement"
                      className="mt-0.5"
                    />
                    <label htmlFor="illiquidity" className="text-sm text-foreground-muted cursor-pointer leading-relaxed">
                      I understand that this investment is illiquid and cannot be easily sold before the project exit
                    </label>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setInvestStep(2)} className="flex-1 rounded-full border-black/10">
                    Back
                  </Button>
                  <Button
                    className="flex-1 bg-primary hover:bg-primary-hover text-white rounded-full"
                    onClick={handleInvest}
                    disabled={!acknowledgements.risk || !acknowledgements.illiquidity || investing}
                    data-testid="confirm-invest-button"
                  >
                    {investing ? (
                      <span className="inline-flex items-center gap-2">
                        <FlippexLoader size={24} animate={false} label="Processing your investment" />
                        Processing
                      </span>
                    ) : (
                      'Confirm Investment'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Mobile sticky invest CTA */}
      <div
        className={`lg:hidden fixed ${isApp ? 'bottom-16 md:bottom-0' : 'bottom-0'} left-0 right-0 z-40 px-5 pt-3 pb-5 bg-white/95 backdrop-blur-md border-t border-black/[0.06] transition-all duration-300 ${
          showStickyCta && !isExited ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'
        }`}
        data-testid="mobile-sticky-invest-bar"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.14em] text-foreground-muted">From</p>
            <p className="text-lg font-heading font-semibold leading-tight">
              €{project.min_investment?.toLocaleString()}
            </p>
          </div>
          {!user ? (
            <Button
              className="flex-1 bg-primary hover:bg-primary-hover text-white rounded-full py-6 text-base font-medium"
              onClick={() => setShowAuthModal(true)}
              data-testid="mobile-login-to-invest-button"
            >
              Login to Invest
            </Button>
          ) : !isEligible ? (
            <Button
              className="flex-1 bg-primary hover:bg-primary-hover text-white rounded-full py-6 text-base font-medium"
              onClick={() => navigate(onboardingPath)}
              data-testid="mobile-complete-onboarding-button"
            >
              Complete Onboarding
            </Button>
          ) : canInvestNow ? (
            <Button
              className="flex-1 bg-primary hover:bg-primary-hover text-white rounded-full py-6 text-base font-medium"
              onClick={() => setShowInvestModal(true)}
              data-testid="mobile-invest-button"
            >
              Invest Now
            </Button>
          ) : (
            <Button className="flex-1 rounded-full py-6 text-base" disabled>
              Not Available
            </Button>
          )}
        </div>
      </div>

      {!isApp && <Footer />}
    </div>
  );
};

export default ProjectDetailPage;
